import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, SectionTitle, WhatsAppIcon } from "./shared";
import { waLink } from "../../lib/api";
import { useSite } from "../../context/SiteContext";

const AREAS = [
  { name: "Belo Horizonte", to: "/celebrante-de-casamento-belo-horizonte", note: "Atendimento direto na capital" },
  { name: "Região Metropolitana", to: "/celebrante-casamento-regiao-metropolitana-bh", note: "Mesma faixa de investimento de BH" },
  { name: "Nova Lima", to: "/celebrante-casamento-nova-lima", note: "Aos pés da Serra do Curral" },
  { name: "Contagem", to: "/celebrante-casamento-contagem", note: "Espaços de eventos da RMBH" },
  { name: "Betim", to: "/celebrante-casamento-betim", note: "Sítios e casamentos ao ar livre" },
  { name: "Esmeraldas", to: "/celebrante-casamento-esmeraldas", note: "Onde Padre Lucas exerce seu ministério" },
  { name: "Sete Lagoas", to: "/celebrante-casamento-sete-lagoas", note: "Capital dos casamentos no campo" },
  { name: "Campo das Vertentes", to: "/celebrante-de-casamento-campo-das-vertentes", note: "Barbacena, Tiradentes, São João del-Rei" },
  { name: "Todo o interior de Minas", to: "/celebrante-de-casamento-minas-gerais", note: "Mediante consulta de disponibilidade" },
];

export default function NearMe() {
  const { content } = useSite();
  const c = content?.contacts || {};

  return (
    <section id="perto-de-mim" className="py-20 lg:py-28 bg-warmwhite" data-testid="near-me-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Eyebrow>Onde celebramos</Eyebrow>
            <SectionTitle>Procurando um celebrante de casamento perto de mim?</SectionTitle>
            <p className="mt-4 text-base font-light text-cocoa leading-relaxed">
              Se você está em Belo Horizonte, na Região Metropolitana ou no interior de Minas Gerais,
              Padre Lucas Macieira celebra o casamento de vocês no lugar que tiver significado para a
              história do casal — igreja, sítio, chácara, fazenda, salão ou residência. A cerimônia
              vai até vocês.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {AREAS.map((area, i) => (
            <Reveal key={area.to} delay={(i % 3) * 0.08}>
              <Link
                to={area.to}
                className="group flex items-start gap-4 bg-cream border border-gold/20 rounded-2xl p-6 h-full hover:border-gold/50 hover:shadow-xl hover:shadow-gold/10 hover:-translate-y-1 transition-all duration-500"
                data-testid={`near-me-card-${i}`}
              >
                <div className="shrink-0 w-10 h-10 rounded-full bg-sand flex items-center justify-center text-golddark group-hover:bg-gold group-hover:text-white transition-colors duration-500">
                  <MapPin className="w-4 h-4" />
                </div>
                <div>
                  <h3 className="text-lg font-serif text-ink leading-snug">{area.name}</h3>
                  <p className="mt-1 text-sm font-light text-cocoa leading-relaxed">{area.note}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-[0.15em] text-golddark">
                    Ver atendimento <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-10 bg-sand border-l-4 border-gold rounded-r-2xl p-6 sm:p-8" data-testid="near-me-note">
            <p className="text-sm sm:text-base font-light text-cocoa leading-relaxed">
              <span className="font-semibold text-olive">Sua cidade não está na lista? </span>
              Padre Lucas atende todo o estado de Minas Gerais mediante disponibilidade de agenda.
              Custos de deslocamento, quando necessários, são apresentados como orçamento prévio,
              sem surpresas.
            </p>
            <a
              href={waLink(c.whatsapp_number, "Olá, Padre Lucas! Gostaria de saber se o senhor celebra casamentos na minha cidade.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 bg-whatsapp hover:bg-whatsappdark text-white text-sm font-semibold rounded-full px-6 py-3 transition-colors duration-300"
              data-testid="near-me-whatsapp-button"
            >
              <WhatsAppIcon className="w-4 h-4" />
              Consultar minha cidade no WhatsApp
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
